import type { CvSectionId, CvSectionsConfig } from '@tabbio/types';

export const DEFAULT_CV_SECTION_ORDER: readonly CvSectionId[] = [
  'summary',
  'careerHighlights',
  'skills',
  'experience',
  'projects',
  'education',
  'certifications',
  'languages',
];

const SECTION_SET = new Set<string>(DEFAULT_CV_SECTION_ORDER);

export function isCvSectionId(value: unknown): value is CvSectionId {
  return typeof value === 'string' && SECTION_SET.has(value);
}

function normalizeCvSectionIncludeOrder(
  order?: readonly unknown[] | null,
): CvSectionId[] {
  if (!order?.length) return [];

  const seen = new Set<string>();
  const normalized: CvSectionId[] = [];
  for (const id of order) {
    if (!isCvSectionId(id) || seen.has(id)) continue;
    seen.add(id);
    normalized.push(id);
  }
  return normalized;
}

export function normalizeCvSectionOrder(
  order?: readonly unknown[] | null,
): CvSectionId[] {
  const normalized = normalizeCvSectionIncludeOrder(order);
  return normalized.length > 0
    ? normalized
    : [...DEFAULT_CV_SECTION_ORDER];
}

export function normalizeCvSectionsConfig(
  input: unknown,
): CvSectionsConfig {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return null;
  }

  const order = (input as { order?: unknown }).order;
  if (!Array.isArray(order)) return null;

  const normalized = normalizeCvSectionIncludeOrder(order);
  return normalized.length > 0 ? { order: normalized } : null;
}

export function getVisibleCvSections(
  config?: CvSectionsConfig,
): CvSectionId[] {
  return normalizeCvSectionOrder(config?.order);
}

export function getHiddenCvSections(
  config?: CvSectionsConfig,
): CvSectionId[] {
  const visible = new Set(getVisibleCvSections(config));
  return DEFAULT_CV_SECTION_ORDER.filter(
    (sectionId) => !visible.has(sectionId),
  );
}

export function isCvSectionVisible(
  config: CvSectionsConfig | undefined,
  sectionId: CvSectionId,
): boolean {
  return getVisibleCvSections(config).includes(sectionId);
}

export function insertCvSectionInDefaultPosition(
  order: readonly CvSectionId[],
  sectionId: CvSectionId,
): CvSectionId[] {
  if (order.includes(sectionId)) return [...order];

  const next = [...order];
  const sectionIndex = DEFAULT_CV_SECTION_ORDER.indexOf(sectionId);
  const insertBefore = next.findIndex(
    (id) => DEFAULT_CV_SECTION_ORDER.indexOf(id) > sectionIndex,
  );

  if (insertBefore === -1) {
    next.push(sectionId);
  } else {
    next.splice(insertBefore, 0, sectionId);
  }

  return next;
}

export function ensureCvSectionVisible(
  config: CvSectionsConfig | undefined,
  sectionId: CvSectionId,
): CvSectionsConfig | undefined {
  if (!config) return config;
  const order = normalizeCvSectionOrder(config.order);
  if (order.includes(sectionId)) return { order };
  return { order: insertCvSectionInDefaultPosition(order, sectionId) };
}

export function setCvSectionVisibility(
  config: CvSectionsConfig | undefined,
  sectionId: CvSectionId,
  visible: boolean,
): CvSectionsConfig {
  const order = normalizeCvSectionOrder(config?.order);
  if (visible) {
    return { order: insertCvSectionInDefaultPosition(order, sectionId) };
  }

  const next = order.filter((id) => id !== sectionId);
  return { order: next.length > 0 ? next : order };
}

export function moveCvSection(
  config: CvSectionsConfig | undefined,
  sectionId: CvSectionId,
  direction: 'up' | 'down',
): CvSectionsConfig {
  const order = normalizeCvSectionOrder(config?.order);
  const currentIndex = order.indexOf(sectionId);
  if (currentIndex === -1) return { order };

  const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
  if (targetIndex < 0 || targetIndex >= order.length) return { order };

  const next = [...order];
  const [item] = next.splice(currentIndex, 1);
  next.splice(targetIndex, 0, item);
  return { order: next };
}
